import { isEqual } from 'es-toolkit'
import type { inferParserType } from 'nuqs/server'
import { createParser, parseAsArrayOf, parseAsInteger } from 'nuqs/server'

const adultsParser = parseAsInteger
const childrenParser = parseAsArrayOf(parseAsInteger)
const allocationParser = createParser<PaxAllocation[]>({
  eq: isEqual,
  serialize: (value: PaxAllocation[] | null | undefined): string => {
    if (!value || value.length === 0) return ''

    const entries: string[] = []
    for (const entry of value) {
      if (!entry) continue
      const adults = encodeNumber(entry.adults)
      const children = encodeDelimitedNumericArray(entry.children ?? [])
      entries.push([entry.id, adults, children].filter(Boolean).join(':'))
    }

    return encodeDelimitedArray(entries, ',')
  },

  parse: (value: string | (string | null)[] | null | undefined): PaxAllocation[] | null => {
    const entries = decodeDelimitedArray(value, ',')
    if (entries == null) return null

    const allocations: PaxAllocation[] = []
    for (const entry of entries) {
      if (!entry) continue
      const [id, adultsStr, childrenStr] = entry.split(':')
      const adultsNum = decodeNumber(adultsStr)
      const childrenNum = decodeDelimitedNumericArray(childrenStr)?.filter(
        (c): c is number => c !== null
      )
      if (!adultsNum) continue
      allocations.push({ id, adults: adultsNum, children: childrenNum ?? [] })
    }
    return allocations
  },
})

export const paxParser = {
  adults: adultsParser.withDefault(2),
  children: childrenParser.withDefault([]),
  allocation: allocationParser.withDefault([{ id: 'default', adults: 2, children: [] }]),
}

export const getDefaultAllocation = ({
  adults = 2,
  children = [],
}: Partial<Pick<PaxParams, 'adults' | 'children'>>) => {
  return [{ id: 'default', adults, children }]
}

export function createAllocation(pax: PaxParams) {
  return { adults: pax.adults, children: pax.children, id: crypto.randomUUID() }
}

export type PaxParams = Omit<inferParserType<typeof paxParser>, 'allocation'> & {
  allocation?: PaxAllocation[] | null
}
export type PaxAllocation = { id: string; adults: number; children: number[] }

const encodeDelimitedArray = (values: string[], delimiter: string): string => {
  return values.join(delimiter)
}

const decodeDelimitedArray = (
  input: string | (string | null)[] | null | undefined,
  delimiter: string
): string[] | undefined => {
  if (input == null) return undefined
  const segments: string[] = []
  if (Array.isArray(input)) {
    for (const part of input) {
      if (!part) continue
      segments.push(...part.split(delimiter))
    }
  } else if (typeof input === 'string') {
    if (input.length === 0) return []
    segments.push(...input.split(delimiter))
  } else {
    return undefined
  }
  return segments
}

const encodeDelimitedNumericArray = (values: number[]): string => {
  return values.map(v => String(Math.trunc(v))).join('_')
}

const decodeDelimitedNumericArray = (value: string | undefined): (number | null)[] | undefined => {
  if (value == null) return undefined
  if (value === '') return []
  return value.split('_').map(token => {
    const parsed = Number.parseInt(token, 10)
    return Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : null
  })
}

const encodeNumber = (value: number | null | undefined): string => {
  if (value == null) return ''
  return String(Math.trunc(value))
}

const decodeNumber = (value: string | undefined): number | null => {
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : null
}
